import type { Vec2 } from '@sw/schema'
import { distance } from './vec.js'

/**
 * One Euro filter: a low-pass whose cutoff rises with speed.
 *
 * Chosen over a fixed low-pass because the two failure modes pull in opposite
 * directions — heavy smoothing kills the jitter of a listener standing still
 * but makes a runner's sound field lag metres behind them, while light
 * smoothing tracks the runner and leaves the stationary listener swimming.
 * Raising the cutoff with movement gets both.
 */
class LowPass {
  private value: number | undefined

  filter(input: number, alpha: number): number {
    this.value = this.value === undefined ? input : alpha * input + (1 - alpha) * this.value
    return this.value
  }

  get last(): number | undefined {
    return this.value
  }

  reset(): void {
    this.value = undefined
  }
}

export interface OneEuroOptions {
  /** Cutoff at rest, in Hz. Lower is smoother and laggier. */
  minCutoff?: number
  /** How aggressively the cutoff opens up with speed. */
  beta?: number
  /** Cutoff for the speed estimate itself. */
  derivativeCutoff?: number
}

export class OneEuroFilter {
  private readonly minCutoff: number
  private readonly beta: number
  private readonly derivativeCutoff: number
  private readonly value = new LowPass()
  private readonly derivative = new LowPass()
  private lastTimeMs: number | undefined
  private lastRaw: number | undefined

  constructor(options: OneEuroOptions = {}) {
    // Tuned by sweep against the two cases that matter at the ~1 Hz a phone
    // reports: a listener standing still, and a runner at 4 m/s. These values
    // cut stationary jitter to about 60% while holding a runner within
    // roughly 2 m of their true position. Opening the cutoff further tracks
    // the runner better but leaves the stationary listener swimming, and a
    // sound field that drifts while you stand still is the more obvious fault.
    this.minCutoff = options.minCutoff ?? 0.15
    this.beta = options.beta ?? 0.05
    this.derivativeCutoff = options.derivativeCutoff ?? 0.3
  }

  filter(input: number, timeMs: number): number {
    const dt =
      this.lastTimeMs === undefined ? undefined : Math.max(1e-3, (timeMs - this.lastTimeMs) / 1000)
    this.lastTimeMs = timeMs

    if (dt === undefined || this.lastRaw === undefined) {
      this.lastRaw = input
      return this.value.filter(input, 1)
    }

    const rate = (input - this.lastRaw) / dt
    this.lastRaw = input
    const smoothedRate = this.derivative.filter(rate, alpha(this.derivativeCutoff, dt))
    const cutoff = this.minCutoff + this.beta * Math.abs(smoothedRate)
    return this.value.filter(input, alpha(cutoff, dt))
  }

  reset(): void {
    this.value.reset()
    this.derivative.reset()
    this.lastTimeMs = undefined
    this.lastRaw = undefined
  }
}

function alpha(cutoffHz: number, dtSeconds: number): number {
  const tau = 1 / (2 * Math.PI * cutoffHz)
  return 1 / (1 + tau / dtSeconds)
}

export interface PositionFilterOptions extends OneEuroOptions {
  /**
   * Fixes implying a speed above this are treated as outliers and dropped. GPS
   * occasionally reports a wild position; without this, one bad fix throws the
   * whole sound field across the map for a second.
   */
  maxSpeedMps?: number
}

export interface FilteredPosition {
  position: Vec2
  /** True when the raw fix was discarded as implausible. */
  rejected: boolean
}

export class PositionFilter {
  private readonly x: OneEuroFilter
  private readonly y: OneEuroFilter
  private readonly maxSpeedMps: number
  private lastAccepted: { position: Vec2; timeMs: number } | undefined
  private lastOutput: Vec2 | undefined

  constructor(options: PositionFilterOptions = {}) {
    this.x = new OneEuroFilter(options)
    this.y = new OneEuroFilter(options)
    // Comfortably above a sprint, below anything a GPS glitch produces.
    this.maxSpeedMps = options.maxSpeedMps ?? 15
  }

  update(position: Vec2, timeMs: number): FilteredPosition {
    if (this.lastAccepted !== undefined && this.lastOutput !== undefined) {
      const dt = (timeMs - this.lastAccepted.timeMs) / 1000
      if (dt > 0) {
        const impliedSpeed = distance(this.lastAccepted.position, position) / dt
        if (impliedSpeed > this.maxSpeedMps) {
          return { position: this.lastOutput, rejected: true }
        }
      }
    }

    const filtered = { x: this.x.filter(position.x, timeMs), y: this.y.filter(position.y, timeMs) }
    this.lastAccepted = { position, timeMs }
    this.lastOutput = filtered
    return { position: filtered, rejected: false }
  }

  reset(): void {
    this.x.reset()
    this.y.reset()
    this.lastAccepted = undefined
    this.lastOutput = undefined
  }
}
